import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../lib/toast";
import {
  SalesReturn,
  salesReturnService,
} from "../../services/sales-return.service";

export function SalesReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [salesReturn, setSalesReturn] = useState<SalesReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [releasingLineId, setReleasingLineId] = useState<string | null>(null);

  const loadReturn = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await salesReturnService.getSalesReturn(id);
      setSalesReturn(data);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load sales return",
      );
      navigate("/sales-returns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReturn();
  }, [id]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const releaseToAvailableStock = async (itemId: string) => {
    if (!salesReturn) return;
    const item = salesReturn.items.find((candidate) => candidate.id === itemId);
    if (!item) return;
    if (
      !window.confirm(
        `Confirm ${item.product.name} is sealed, undamaged and saleable. This will add ${item.qtyReturned} to available stock.`,
      )
    )
      return;
    try {
      setReleasingLineId(item.id);
      await salesReturnService.releaseQuarantinedItem(salesReturn, item);
      setSalesReturn((current) =>
        current
          ? {
              ...current,
              items: current.items.map((line) =>
                line.id === item.id
                  ? {
                      ...line,
                      condition: "SEALED_SALEABLE",
                      stockDisposition: "AVAILABLE",
                    }
                  : line,
              ),
            }
          : current,
      );
      showToast.success("Returned item added to available stock");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to release returned stock",
      );
    } finally {
      setReleasingLineId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!salesReturn) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Sales return not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/sales-returns")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Sales Returns
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Return #{salesReturn.returnNumber}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {formatDate(salesReturn.returnDate)} ·{" "}
              {salesReturn.customer?.name || "Walk-in"}
            </p>
          </div>
          <div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              {salesReturn.status}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Return Details
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Customer</p>
                <p className="font-medium text-gray-900">
                  {salesReturn.customer?.name || "Walk-in"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Return Reason</p>
                <p className="font-medium text-gray-900">
                  {salesReturn.returnReason}
                </p>
              </div>
              {salesReturn.invoice?.invoiceNumber && (
                <div>
                  <p className="text-gray-500">Invoice</p>
                  <button
                    onClick={() =>
                      navigate(`/invoices/${salesReturn.invoice?.id}`)
                    }
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    {salesReturn.invoice.invoiceNumber}
                  </button>
                </div>
              )}
              {salesReturn.notes && (
                <div>
                  <p className="text-gray-500">Notes</p>
                  <p className="font-medium text-gray-900">
                    {salesReturn.notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Product
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Batch
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Rate
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Tax
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Stock handling
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {salesReturn.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-3">
                        <p className="font-medium text-gray-900">
                          {item.product.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.product.sku}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs font-mono">
                          {item.batch.batchNumber}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {item.qtyReturned}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {formatCurrency(item.rate)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {formatCurrency(item.gstAmount)}
                      </td>
                      <td className="px-3 py-3">
                        {(item.stockDisposition ?? item.disposition) ===
                        "AVAILABLE" ? (
                          <span className="inline-flex rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                            Available stock
                          </span>
                        ) : (item.stockDisposition ?? item.disposition) ===
                          "QUARANTINE" ? (
                          <div className="space-y-2">
                            <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-900">
                              Quarantined
                            </span>
                            <button
                              type="button"
                              disabled={releasingLineId === item.id}
                              onClick={() =>
                                void releaseToAvailableStock(item.id)
                              }
                              className="block text-left text-xs font-medium text-blue-700 hover:text-blue-900 disabled:opacity-50"
                            >
                              {releasingLineId === item.id
                                ? "Updating stock…"
                                : "Mark sealed & add to stock"}
                            </button>
                          </div>
                        ) : (
                          <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800">
                            {(
                              item.stockDisposition ??
                              item.disposition ??
                              "Not recorded"
                            ).replace(/_/g, " ")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-medium">
                        {formatCurrency(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Summary
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium">
                  {formatCurrency(salesReturn.subtotal)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Tax</span>
                <span className="font-medium">
                  {formatCurrency(salesReturn.taxAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between text-base">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(salesReturn.totalAmount)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h2>
            <p className="text-sm text-gray-600">
              Return status is posted atomically by the canonical sale-return
              command and cannot be changed through the legacy invoice route.
            </p>
            <p className="mt-3 text-sm text-gray-600">
              Sealed, undamaged items are available for sale immediately. Opened
              or uncertain items remain quarantined until a staff member
              confirms they are saleable.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
